import React, {
  type ComponentProps,
  forwardRef,
  useEffect,
  useState,
} from 'react'
import { twMerge } from 'tailwind-merge'
import { tv, type VariantProps } from 'tailwind-variants'

const inputVariants = tv({
  base: 'absolute block bg-white text-text-primary text-base duration-300 group-data-[disabled=false]:group-hover:text-text-primary group-data-[disabled=false]:group-hover:cursor-pointer group-data-[active=true]:text-text-primary group-data-[error=true]:group-data-[active=true]:text-error group-data-[disabled=true]:text-borderColor/30',
  variants: {
    variant: {
      form: 'group-data-[active=true]:mb-[4.875rem] group-data-[active=true]:ml-[-0.75rem] group-data-[active=true]:text-sm',
      filter:
        'block bg-white duration-300 group-data-[active=true]:mb-[3rem] group-data-[active=true]:px-1 group-data-[active=true]:ml-[-4px] group-data-[active=true]:text-xs',
    },
  },
  defaultVariants: {
    variant: 'filter',
  },
})

type InputProps = VariantProps<typeof inputVariants> &
  ComponentProps<'input'> & {
    label: string
    errorMessage?: string | null
    defaultValue?: string
    onFocusOut?: (e: React.ChangeEvent<HTMLInputElement>) => any
    icon?: React.ReactNode // Novo prop para o ícone
    autoComplete?:
      | 'name'
      | 'given-name'
      | 'family-name'
      | 'nickname'
      | 'username'
      | 'current-password'
      | 'one-time-code'
      | 'organization'
      | 'address-line1'
      | 'cc-number'
      | 'cc-exp'
      | 'cc-exp-month'
      | 'cc-exp-year'
      | 'cc-name'
      | 'cc-csc'
      | 'off'
      | 'email'
  }

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    variant,
    className,
    errorMessage = null,
    defaultValue,
    onFocusOut,
    icon, // Novo prop sendo desestruturado
    autoComplete,
    ...props
  },
  ref,
): JSX.Element {
  delete props.onBlur
  delete props.onFocus

  const [isFocused, setIsFocused] = useState(!!props.value || !!defaultValue)
  const hasError = !!errorMessage
  const isDisabled = props.disabled
  const placeholder = isFocused ? props.placeholder : ''

  function handleInputFocus(): void {
    setIsFocused(true)
  }

  function handleInputBlur(e: React.ChangeEvent<HTMLInputElement>): void {
    if (onFocusOut) {
      onFocusOut(e)
    }

    if (!e.target.value) {
      setIsFocused(false)
    }
  }

  useEffect(() => {
    if (!!defaultValue || !!props.value) {
      setIsFocused(true)
    }
  }, [defaultValue])

  return (
    <div className={twMerge('w-full relative space-y-1.5', className)}>
      <div
        data-active={isFocused || !!props.value || !!defaultValue}
        data-error={hasError}
        data-disabled={isDisabled}
        className="
            group mt-auto p-3 h-10 w-full flex items-center justify-start border border-border-inputDefault rounded-[0.375rem] transition-all
          data-[error=false]:data-[disabled=false]:hover:border-text-primary enabled:hover:cursor-pointer
          data-[disabled=true]:border-borderColor/30 data-[disabled=true]:text-borderColor/30
          data-[error=true]:border-error
          focus-within:border-text-primary"
      >
        <label className={inputVariants({ variant })}>{label}</label>
        <input
          value={props.value}
          autoComplete={autoComplete}
          ref={ref}
          className="
              bg-transparent text-base leading-none w-full z-10 border-none text-text-primary
              focus:outline-none group-data-[disabled=true]:text-borderColor/30"
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          onInput={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (props.type === 'number') {
              const value = event.target.value
              const newValue = value.replace(/\D/g, '')
              event.target.value = newValue
            }
          }}
          {...props}
        />
        {icon && (
          <div className="ml-2 flex items-center text-gray-500">{icon}</div>
        )}
      </div>

      {hasError && (
        <span className="block text-error text-sm">{errorMessage}</span>
      )}
    </div>
  )
})
